import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchStats } from "../lib/api";
import { useGame } from "../context/GameContext";
import { formatNumber } from "../lib/format";
import { Map, Globe, Gauge, Trophy, Users } from "lucide-react";

const StatCard = ({ icon, label, value, sub, testId, highlight }) => (
  <div
    className="tac-card scanlines p-4 rise relative overflow-hidden"
    data-testid={testId}
  >
    <span className="corner-tr" />
    <span className="corner-bl" />
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="hud-label mb-2">{label}</div>
        <div
          className="font-head font-extrabold uppercase truncate"
          style={{ color: highlight ? "var(--highlight)" : "var(--text-primary)", fontSize: 24, lineHeight: 1.15, letterSpacing: "0.015em" }}
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </div>
        {sub && <div className="hud-caption mt-2 truncate">{sub}</div>}
      </div>
      <div
        className="w-10 h-10 flex items-center justify-center border shrink-0"
        style={{ color: "var(--primary-accent)", borderColor: "var(--border-strong)" }}
      >
        {icon}
      </div>
    </div>
  </div>
);

const StatsBanner = () => {
  const { gameId, game } = useGame();
  const { data, isLoading } = useQuery({
    queryKey: ["stats", gameId],
    queryFn: () => fetchStats(gameId),
    enabled: !!gameId,
    refetchInterval: 15000,
  });

  if (!game) return null;

  return (
    <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4" data-testid="stats-banner">
      <StatCard
        icon={<Users className="w-5 h-5" />}
        label="PLAYERS ONLINE"
        value={isLoading ? "…" : formatNumber(data?.total_players)}
        sub={`${data?.online_servers ?? 0}/${data?.total_servers ?? 0} servers up`}
        testId="stat-players-online"
        highlight
      />
      <StatCard
        icon={<Globe className="w-5 h-5" />}
        label="PEAK REGION"
        value={isLoading ? "…" : data?.peak_region}
        sub={`${formatNumber(data?.peak_region_players)} players`}
        testId="stat-peak-region"
      />
      <StatCard
        icon={<Map className="w-5 h-5" />}
        label="TOP MAP"
        value={isLoading ? "…" : data?.top_map}
        sub={`${data?.top_map_servers ?? 0} servers running`}
        testId="stat-top-map"
      />
      <StatCard
        icon={<Trophy className="w-5 h-5" />}
        label="TOP MODE"
        value={isLoading ? "…" : data?.top_mode}
        sub={`${data?.top_mode_servers ?? 0} servers running`}
        testId="stat-top-mode"
      />
      <StatCard
        icon={<Gauge className="w-5 h-5" />}
        label="LOWEST PING"
        value={isLoading || !data?.lowest_ping_server ? "…" : `${data.lowest_ping_server.ping}ms`}
        sub={data?.lowest_ping_server ? data.lowest_ping_server.region : "—"}
        testId="stat-lowest-ping"
      />
    </section>
  );
};

export default StatsBanner;
